import { Trans, t } from "@web-monorepo/i18n/macro";
import { LoadingMojo } from "@web-monorepo/molecules";
import { DDSButton } from "@web-monorepo/dds";
import { clsx } from "clsx";
import { useEffect, useRef, useState } from "react";
import { Student } from "src/pods/students";
import logEvent from "src/utils/logEvent";
import styles from "./RandomDisplay.module.css";

type RandomDisplayProps = {
  displayRandomStudent: (students?: Student[]) => void;
  student?: Student;
  students?: Student[];
  isRestart?: boolean;
  fullscreen?: boolean;
};

const MAX_CARDS = 6;
const ANIMATION_DURATION_MS = 1500;

export function RandomDisplay({ displayRandomStudent, student, students, isRestart, fullscreen }: RandomDisplayProps) {
  const [shownStudent, setShownStudent] = useState<Student | undefined>(student);
  const [isRunning, setIsRunning] = useState(true);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Animation lives in a ref so the student-change effect can fire it without
  // depending on `students` (which would make the effect re-run when the
  // roster shifts and restart the animation mid-flight). Updated each render
  // so it always closes over the latest students list.
  const animateRef = useRef<(chosenStudent: Student | undefined) => void>(() => {});
  animateRef.current = (chosenStudent: Student | undefined) => {
    if (timerRef.current) clearInterval(timerRef.current);
    let cardIndex = 0;
    const nextFrame = () => {
      if (cardIndex++ < MAX_CARDS) {
        setShownStudent((current) => {
          const others = students?.filter((s) => s !== current);
          return others?.[Math.floor(Math.random() * others.length)];
        });
        setIsRunning(true);
      } else {
        setShownStudent(chosenStudent);
        setIsRunning(false);
        if (timerRef.current) clearInterval(timerRef.current);
      }
    };
    nextFrame();
    timerRef.current = setInterval(nextFrame, ANIMATION_DURATION_MS / MAX_CARDS);
  };

  // Setup/teardown: log open/close + ensure timer is cleared on unmount.
  useEffect(() => {
    logEvent({ eventName: "random.result.open" });
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      logEvent({ eventName: "random.result.close" });
    };
  }, []);

  // Run animation on mount (unless restarting from a saved state) and again
  // whenever the picked student changes. `isRestart` is only inspected on the
  // initial run; a ref freezes its mount-time value so we don't re-evaluate
  // it on subsequent prop changes.
  const isRestartOnMountRef = useRef(isRestart);
  const hasRunInitialRef = useRef(false);
  useEffect(() => {
    if (!hasRunInitialRef.current) {
      hasRunInitialRef.current = true;
      if (student && !isRestartOnMountRef.current) {
        animateRef.current(student);
      }
      return;
    }
    animateRef.current(student);
  }, [student]);

  if (!students || !shownStudent) {
    return (
      <section className={clsx(styles.card, fullscreen && styles.cardFullscreen)}>
        <LoadingMojo inline text={t`Please wait...`} />
      </section>
    );
  }

  const lastInitial = shownStudent.lastName ? `${shownStudent.lastName.slice(0, 1)}.` : "";
  const displayName = `${shownStudent.firstName} ${lastInitial}`.trim();

  const tryAgain = () => {
    if (students.length === 0) return;
    displayRandomStudent(students);
    logEvent({ eventName: "random.pickagain" });
  };

  return (
    <div className={styles.centered}>
      <div className={styles.container}>
        <h2 className={styles.announce}>
          <Trans>Your random pick is:</Trans>
        </h2>
        <section className={clsx(styles.card, fullscreen && styles.cardFullscreen)}>
          <img className={styles.avatar} src={shownStudent.avatar} alt="" />
          {/* Animation frames cycle visually but shouldn't be announced. */}
          {isRunning && (
            <p className={styles.name} aria-hidden="true">
              {displayName}
            </p>
          )}
          {/*
            Final name is wrapped in a live region rather than putting
            role="status" directly on the <p>: aria-allowed-role forbids
            role="status" on h2/p but allows it on div. aria-label is set
            unconditionally so the announcement context ("Your random pick
            is:") is included when the live region fires on content change.
          */}
          <div role="status" aria-live="polite" aria-label={t`Your random pick is:`}>
            <p className={styles.name}>{isRunning ? "" : displayName}</p>
          </div>
        </section>
        <div className={styles.pick}>
          <DDSButton data-name="random_display:render:random_pick_again" onClick={tryAgain} variant="secondary">
            <Trans>Pick again</Trans>
          </DDSButton>
        </div>
      </div>
    </div>
  );
}

export default RandomDisplay;
